import React, { useEffect, useRef, useState } from 'react';
import SlipAlert from '../SlipAlert';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── My Tasks bell ────────────────────────────────────────────────────────────
// Sits at the right end of the header row. Two things:
//   · a badge + panel with everything waiting on the logged-in user (unmatched incoming money for
//     the accounts team, plus their own open jobs);
//   · a nagging alert modal for the urgent subset — a job due today/tomorrow or already overdue, and
//     money that has sat unmatched for more than 2 days. Owner rules (2026-07-31): it closes ONLY on a
//     click, then snoozes 4 hours and comes back — but every reappearance is driven by freshly polled
//     data, so once the work is actually done it simply stops coming. It never reloads or navigates:
//     the poll is a background fetch, so whatever you were typing stays exactly where it was.
// Read-only: clicking a row just takes you to the module that owns it.

// Owner 2026-08-25 (same rule as the Jobs lists): hide a "【1】" quantity marker in job titles —
// only a quantity other than one is information. Display-only.
const stripQtyOne = (s: any): string => String(s || '').replace(/\s*【\s*1\s*】/g, ' ').replace(/\s{2,}/g, ' ').trim();
const fmtAmt = (n: any) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };
// "3 days late" / "due today" / "due tomorrow" — the number people actually act on.
function lateLabel(it: any, t: any): string {
  const n = Number(it.lateDays) || 0;
  if (n > 0) return t('mt_late_days', { n });
  if (n === 0 && (it.dueSoon || it.overdue)) return t('mt_due_today');
  if (n === -1) return t('mt_due_tomorrow');
  return '';
}
const SNOOZE_MS = 4 * 3600_000;   // dismissed → quiet for 4 hours, then ask again
// Keyed per user: two people sharing a browser (or one person switching accounts) must not inherit
// each other's snooze and wonder why nothing ever pops up.
const snoozeKey = (who: string) => `mf_tasks_snooze_until:${(who || 'anon').toLowerCase()}`;

// Polls in the background. Never navigates, never reloads — it only swaps state, so an open form or
// a half-typed email is untouched when the numbers change.
export function useMyTasks(pollMs = 300_000) {
  const [data, setData] = useState<any>(null);
  useEffect(() => {
    let dead = false;
    const load = () => api.myTasks().then((d: any) => { if (!dead) setData(d); }).catch(() => {});
    load();
    const h = setInterval(load, pollMs);
    // Acting on a task (e.g. snoozing a close-nudge) must update the list now, not at the next poll.
    window.addEventListener('mf-tasks-reload', load);
    return () => { dead = true; clearInterval(h); window.removeEventListener('mf-tasks-reload', load); };
  }, [pollMs]);
  return data;
}

export function TasksBell({ data, onGo }: { data: any; onGo: (source: string, item?: any) => void }) {
  const { t } = useTranslation();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const open = !!at;
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setAt(null); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAt(null); };
    const close = () => setAt(null);
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', esc);
    window.addEventListener('resize', close);
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc); window.removeEventListener('resize', close); };
  }, [open]);

  const bank = data?.bank || [];
  const jobs = data?.jobs || [];
  const closeNudges = data?.closeNudges || [];
  const awaitingQuotes = data?.awaitingQuotes || [];
  const n = bank.length + jobs.length + closeNudges.length + awaitingQuotes.length;
  const urgent = (data?.alerts || []).length;

  const row = (it: any, i: number) => (
    <button key={`${it.kind}-${it.id}-${i}`} title={t('mt_open_hint')} onClick={() => { setAt(null); onGo(it.source, it); }}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <i className={`ti ti-${it.kind === 'bank' ? 'cash-banknote' : 'briefcase'}`} style={{ fontSize: 15, color: it.alert ? '#DC2626' : 'var(--text-tertiary)', marginTop: 2, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stripQtyOne(it.title)}</span>
        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {it.kind === 'bank' ? `${fmtAmt(it.amount)} THB · ${it.subtitle}` : it.subtitle}
        </span>
        <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <i className="ti ti-user" /> {it.owner || '—'}
        </span>
      </span>
      <span style={{ textAlign: 'right', whiteSpace: 'nowrap', marginTop: 2 }}>
        <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: it.alert ? '#DC2626' : 'var(--text-tertiary)' }}>{fmtDate(it.date)}</span>
        {lateLabel(it, t) && <span style={{ display: 'block', fontSize: 10, fontWeight: 800, color: it.overdue ? '#DC2626' : '#B45309' }}>{lateLabel(it, t)}</span>}
      </span>
    </button>
  );

  return (
    <>
      <button ref={btnRef} title={t('mt_title')} aria-label={t('mt_title')}
        onClick={() => { if (open) { setAt(null); return; } const r = btnRef.current?.getBoundingClientRect(); setAt(r ? { x: r.right, y: r.bottom } : { x: 0, y: 50 }); }}
        style={{ position: 'relative', width: 34, height: 30, borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: urgent ? '#DC2626' : 'var(--border)', background: open ? 'var(--bg-active)' : 'var(--bg-app)', color: urgent ? '#DC2626' : 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
        <i className="ti ti-bell" style={{ fontSize: 16 }} />
        {n > 0 && (
          <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 17, height: 17, padding: '0 4px', borderRadius: 999, background: urgent ? '#DC2626' : 'var(--text-tertiary)', color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: '17px', textAlign: 'center' }}>{n > 99 ? '99+' : n}</span>
        )}
      </button>
      {open && (
        <div ref={boxRef} style={{ position: 'fixed', top: at!.y + 6, left: Math.max(8, at!.x - 380), width: 380, maxWidth: '94vw', maxHeight: '76vh', overflowY: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-md)', zIndex: 10070, padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 8px 8px' }}>
            <span style={{ fontSize: 12.5, fontWeight: 800 }}>{t('mt_title')}</span>
            {urgent > 0 && <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#FEE2E2', color: '#B91C1C' }}>{t('mt_urgent', { n: urgent })}</span>}
            <span style={{ flex: 1 }} />
            {urgent > 0 && (
              <button onClick={() => { Object.keys(localStorage).filter(k => k.startsWith('mf_tasks_snooze_until')).forEach(k => localStorage.removeItem(k)); setAt(null); window.dispatchEvent(new Event('mf-tasks-unsnooze')); }}
                style={{ border: '1px solid var(--border-strong)', background: 'var(--bg-app)', borderRadius: 7, padding: '3px 9px', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                {t('mt_show_alert')}
              </button>
            )}
          </div>
          {n === 0 && <div style={{ padding: '18px 10px', textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary)' }}><i className="ti ti-circle-check" style={{ color: '#15803D' }} /> {t('mt_none')}</div>}
          {bank.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '6px 10px 3px' }}>{t('mt_bank')} ({bank.length})</div>
            {bank.slice(0, 30).map(row)}
          </>)}
          {jobs.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 10px 3px' }}>{t('mt_jobs')} ({jobs.length})</div>
            {jobs.slice(0, 30).map(row)}
          </>)}
          {/* Completed jobs still sitting in Active Jobs. Two ways out that are NOT "close it":
              later (back in 24h) and stop asking (kept open on purpose). */}
          {/* Quotations parked "Awaiting information" for over a week — creator's own nudge
              (Calem 2026-09-04, option B). Click opens the quotation. */}
          {awaitingQuotes.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 10px 3px' }}>{t('mt_awaiting_quotes')} ({awaitingQuotes.length})</div>
            {awaitingQuotes.slice(0, 30).map((it: any, i: number) => (
              <div key={`aq-${it.id}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 10px', borderRadius: 8 }}>
                <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>⏳</span>
                <button onClick={() => { setAt(null); window.location.assign(`/m/peak?tab=quotation&open=${it.id}`); }}
                  style={{ display: 'block', flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.docNo} · {it.title}</span>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{t('mt_awaiting_hint', { n: it.days })}{it.note ? ` · ${it.note}` : ''}</span>
                </button>
              </div>
            ))}
          </>)}
          {closeNudges.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 10px 3px' }}>{t('mt_close_ready')} ({closeNudges.length})</div>
            {closeNudges.slice(0, 30).map((it: any, i: number) => (
              <div key={`cn-${it.id}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 10px', borderRadius: 8 }}>
                <i className="ti ti-circle-check" style={{ fontSize: 15, color: '#15803D', marginTop: 2, flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <button onClick={() => { setAt(null); onGo(it.source, it); }} title={t('mt_open_hint')}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>#{it.jobNo} {stripQtyOne(it.title)}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>{it.subtitle || '—'} · {t('mt_close_hint')}</span>
                  </button>
                  <span style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <button onClick={async () => { try { await api.jobCloseNudgeSnooze(it.id); window.dispatchEvent(new Event('mf-tasks-reload')); } catch (e: any) { alert(e.message); } }}
                      style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' }}>{t('mt_close_later')}</button>
                    <button onClick={async () => { try { await api.jobCloseNudgeMute(it.id); window.dispatchEvent(new Event('mf-tasks-reload')); } catch (e: any) { alert(e.message); } }}
                      title={t('mt_close_mute_hint')}
                      style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>{t('mt_close_mute')}</button>
                  </span>
                </span>
              </div>
            ))}
          </>)}
        </div>
      )}
    </>
  );
}

// The alert. Closes ONLY on a click (no backdrop dismiss, no Esc) — that is the point. Dismissing
// snoozes it for 4 hours; when the snooze lapses it re-evaluates the LATEST poll, so items finished
// in the meantime never nag again.
export function TasksAlert({ data, onGo, who = '' }: { data: any; onGo: (source: string, item?: any) => void; who?: string }) {
  const { t } = useTranslation();
  const KEY = snoozeKey(who);
  // A stored value that is missing, blank or corrupt must read as "not snoozed", never NaN —
  // `Date.now() < NaN` is false, so a NaN here would leave the dialog up forever.
  const readSnooze = (k: string): number => {
    try { const n = Number(localStorage.getItem(k) || 0); return Number.isFinite(n) ? n : 0; } catch { return 0; }
  };
  const [snoozeUntil, setSnoozeUntil] = useState<number>(() => readSnooze(KEY));
  // Re-render when the snooze lapses even if no poll landed in the meantime.
  const [, tick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => tick(x => x + 1), 60_000);
    const un = () => setSnoozeUntil(0);
    // Dismissing in one tab must quieten the others too — otherwise the alert looks like it
    // "won't close" in whichever tab was not the one clicked.
    const sync = (e: StorageEvent) => { if (e.key === KEY) setSnoozeUntil(readSnooze(KEY)); };
    window.addEventListener('mf-tasks-unsnooze', un);
    window.addEventListener('storage', sync);
    return () => { clearInterval(h); window.removeEventListener('mf-tasks-unsnooze', un); window.removeEventListener('storage', sync); };
  }, [KEY]);   // eslint-disable-line
  const alerts: any[] = data?.alerts || [];
  if (!alerts.length || Date.now() < snoozeUntil) return null;
  // Owner 2026-08-29 (Nick): "I have read this" appeared to do nothing. The write came FIRST, so
  // anything that made localStorage throw — a browser with site data blocked, private mode, a full
  // quota — threw before the state update and the dialog just sat there. The UI now closes first
  // and the persistence is best-effort: worst case the alert returns on the next page load instead
  // of staying stuck on screen.
  const close = () => {
    const until = Date.now() + SNOOZE_MS;
    setSnoozeUntil(until);
    try { localStorage.setItem(KEY, String(until)); } catch { /* not persisted — still closed here */ }
  };
  const jobs = alerts.filter(a => a.kind === 'job');
  const bank = alerts.filter(a => a.kind === 'bank');
  const line = (a: any, i: number) => (
    <div key={i} title={t('mt_open_hint')} onClick={() => { close(); onGo(a.source, a); }}
      style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
      <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 999, background: a.overdue ? '#FEE2E2' : '#FEF3C7', color: a.overdue ? '#B91C1C' : '#B45309', whiteSpace: 'nowrap' }}>
        {a.overdue ? t('mt_overdue') : t('mt_due_soon')}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {stripQtyOne(a.title)}{a.kind === 'bank' ? ` — ${fmtAmt(a.amount)} THB` : ''}
        </span>
        <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <i className="ti ti-user" /> {a.owner || '—'}{a.kind === 'job' && a.subtitle ? ` · ${a.subtitle}` : ''}
        </span>
      </span>
      <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtDate(a.date)}</span>
        {lateLabel(a, t) && <span style={{ display: 'block', fontSize: 10.5, fontWeight: 800, color: a.overdue ? '#DC2626' : '#B45309' }}>{lateLabel(a, t)}</span>}
      </span>
      <i className="ti ti-external-link" style={{ fontSize: 13, color: 'var(--text-tertiary)', flexShrink: 0 }} />
    </div>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10080, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: 560, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: '#FEF2F2', borderBottom: '1px solid #FECACA' }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 20, color: '#DC2626' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#B91C1C' }}>{t('mt_alert_title')}</div>
            <div style={{ fontSize: 11.5, color: '#B45309' }}>{t('mt_alert_sub', { n: alerts.length })}</div>
          </div>
          <button onClick={close} title={t('mt_ack')} aria-label={t('mt_ack')}
            style={{ border: 'none', background: 'none', color: '#B91C1C', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: '0 2px' }}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
          {jobs.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '8px 4px 4px' }}>{t('mt_jobs')} ({jobs.length})</div>
            {jobs.slice(0, 40).map(line)}
            {jobs.length > 40 && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', padding: '8px 4px' }}>{t('mt_more', { n: jobs.length - 40 })}</div>}
          </>)}
          {bank.length > 0 && (<>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '12px 4px 4px' }}>{t('mt_bank_overdue')} ({bank.length})</div>
            {bank.slice(0, 40).map(line)}
            {bank.length > 40 && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', padding: '8px 4px' }}>{t('mt_more', { n: bank.length - 40 })}</div>}
          </>)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-tertiary)', alignSelf: 'center' }}>{t('mt_snooze_note')}</span>
          <button onClick={close} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: '#DC2626', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {t('mt_ack')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Mounted once in the authed shell so the alert reaches whoever is working, whichever module page
// they happen to be on — not only the dashboard.
export function GlobalTasksAlert({ who = '' }: { who?: string }) {
  const data = useMyTasks();
  // The client-payment-slip popup rides the SAME poll — see SlipAlert for why it takes `data`
  // instead of calling useMyTasks() itself (owner 2026-09-18).
  // Deep-link straight at the row: the target module reads ?focus= and scrolls/highlights it, so the
  // click lands on the actual receipt or job instead of dropping you at the top of a long list.
  return <><TasksAlert data={data} who={who}
    onGo={(source: string, item?: any) => window.open(`/m/${source}${item?.id ? `?focus=${encodeURIComponent(item.id)}` : ''}`, '_blank')} />
    <SlipAlert data={data} /></>;
}

// ── Focus a row after a deep link ────────────────────────────────────────────
// The task list links with ?focus=<row id>. Any list that stamps `data-row-id` on its rows can call
// this and the browser scrolls to that row and flashes it — no hunting through a long table.
export function useFocusRow(ready: boolean) {
  useEffect(() => {
    if (!ready) return;
    const id = new URLSearchParams(window.location.search).get('focus');
    if (!id) return;
    const h = setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${CSS.escape(id)}"]`) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const prev = el.style.boxShadow;
      el.style.transition = 'box-shadow .3s';
      el.style.boxShadow = 'inset 0 0 0 2px #DC2626';
      setTimeout(() => { el.style.boxShadow = prev; }, 2600);
    }, 350);
    return () => clearTimeout(h);
  }, [ready]);
}
